import React, { useContext, useEffect } from "react"
import PropTypes from "prop-types"
import { makeStyles, ThemeProvider, useTheme } from "@material-ui/core/styles"
import { AppBar, Grid, Toolbar, Tab, Tabs, Typography, Box } from "@material-ui/core"
import { HistoryContext } from "./contexts/HistoryContext"
import { SchemaContext } from "./contexts/SchemaContext"
import { UserContext } from "./contexts/UserContext"
import { MessagingContext } from "./contexts/MessagingContext"
import WarningDialog from "./components/modals/WarningDialog"
import UserStatus from "./components/UserStatus"
import UploadsPage from "./pages/UploadsPage"
import ExportsPage from "./pages/ExportsPage"
import StandardsPage from "./pages/StandardsPage"
import SchemaPage from "./pages/SchemaPage"
import TransitionAlert from "./components/alerts/TransitionAlert"
import "./App.css"

function TabPanel(props) {
  const { children, value, index, ...other } = props

  return (
    <Typography
      component="div"
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box p={3}>{children}</Box>}
    </Typography>
  )
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
}

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  }
}

const useStyles = makeStyles(() => ({
  root: {
    flexGrow: 1,
    minHeight: "100vh",
  },
  logo: {
    width: 88,
    marginRight: 10,
  },
  appBar: {
    background: "transparent",
  },
  toolbar: {
    backgroundColor: "rgba(0,0,0,0.8)",
  },
  toolbarInner: {
    justifyContent: "space-between",
  },
  pageContainer: {
    display: "flex",
    flexDirection: "column",
    minHeight: "calc(100vh - 64px)",
  },
  tabs: {
    flex: 1,
  },
  footer: {
    display: "flex",
    flexDirection: "row-r",
    height: 30,
    fontFamily: '"Times New Roman", Times, serif',
    color: "#999",
    justifyContent: "flex-end",
    alignItems: "center",
    padding: 12,
  },
  footerLogoText: {
    paddingTop: 12,
  },
  spireLogo: {
    width: 100,
    marginLeft: 8,
  },
}))

function App() {
  const { user } = useContext(UserContext)
  const { fetchHistory } = useContext(HistoryContext)
  const { fetchSchema, fetchSchemaTable } = useContext(SchemaContext)
  const { errorMessage, successMessage, warningMessage } = useContext(
    MessagingContext
  )

  useEffect(() => {
    fetchHistory(user)
    fetchSchema(user)
    if (user.accessLevel === "ADMIN") {
      fetchSchemaTable(user)
    }
  }, [user, fetchHistory, fetchSchema, fetchSchemaTable])

  const classes = useStyles()
  const [value, setValue] = React.useState(0)

  const theme = useTheme()

  const handleChange = (event, newValue) => {
    setValue(newValue)
  }

  const { accessLevel } = user
  const isAdmin = accessLevel === "ADMIN"

  console.log("APP Version 1.0.18 ***")

  return (
    <ThemeProvider theme={theme}>
      <div className={`App ${classes.root}`}>
        <AppBar className={classes.appBar} position="sticky">
          <Toolbar className={classes.toolbar}>
            <img
              src="images/hm_footer_logo.png"
              alt="logo"
              className={classes.logo}
            />
            <Grid
              container
              className={classes.toolbarInner}
              justifyContent="space-between"
            >
              <Tabs
                item
                value={value}
                onChange={handleChange}
                aria-label="simple tabs example"
                flexGrow="1"
              >
                <Tab
                  disableRipple
                  disableFocusRipple
                  label="Standards & Factors"
                  {...a11yProps(0)}
                />
                <Tab
                  label="Uploads & Reference Tables"
                  disableRipple
                  disableFocusRipple
                  {...a11yProps(1)}
                />

                <Tab
                  disableRipple
                  disableFocusRipple
                  label="Exports & Exceptions"
                  {...a11yProps(2)}
                />
                {isAdmin && (
                  <Tab
                    disableRipple
                    disableFocusRipple
                    label="MTD"
                    {...a11yProps(3)}
                  />
                )}
              </Tabs>
              <UserStatus />
            </Grid>
          </Toolbar>
        </AppBar>
        <div className={classes.pageContainer}>
          {successMessage && (
            <TransitionAlert
              label={successMessage.label}
              description={successMessage.description}
              show={successMessage.show}
              success={true}
            />
          )}
          {errorMessage && (
            <TransitionAlert
              label={errorMessage.label}
              description={errorMessage.description}
              show={errorMessage.show}
              success={false}
            />
          )}
          <div className={classes.tabs}>
            <TabPanel value={value} index={0}>
              <StandardsPage />
            </TabPanel>
            <TabPanel value={value} index={1}>
              <UploadsPage />
            </TabPanel>
            <TabPanel value={value} index={2}>
              <ExportsPage />
            </TabPanel>
            {isAdmin && (
              <TabPanel value={value} index={3}>
                <SchemaPage />
              </TabPanel>
            )}
          </div>
          <div className={classes.footer}>
            <div className={classes.footerLogoText}>
              <i>powered by</i>
            </div>
            <img
              src="images/spire_logo.png"
              alt="spireLogo"
              className={classes.spireLogo}
            />
          </div>
        </div>
      </div>
      {warningMessage && (
        <WarningDialog
          label={warningMessage.label}
          description={warningMessage.description}
          show={warningMessage.show}
          success={false}
        />
      )}
    </ThemeProvider>
  )
}

export default App
